import { useEffect, useState } from 'react';
import './PrivacyPolicy.css';

type Lang = 'en' | 'no';

const STRINGS = {
  en: {
    title: 'PRIVACY',
    intro:
      'We’re Solrick — a small Norwegian wine importer. This page describes what happens to your information when you use our contact form.',
    sections: [
      {
        h: 'What we collect',
        p:
          'Only what you type into the form — your name, your email address, and your message. Nothing else. We don’t run analytics, we don’t set cookies, we don’t share data with social networks, and we don’t track you across the web.',
      },
      {
        h: 'What we do with it',
        p:
          'We read your message and reply to it. That’s the entire purpose. Our legal basis is legitimate interest — you wrote to us, we write back.',
      },
      {
        h: 'Where it lives',
        p:
          'Your message passes through Amazon Web Services — our hosting provider, acting as our data processor — and lands in our inbox at hei@solrick.com. We don’t store form submissions in any database; they live in our email, the way other email does. AWS is certified under the EU–US Data Privacy Framework, which covers the brief transit through their US infrastructure.',
      },
      {
        h: 'How long we keep it',
        p:
          'For as long as the email thread is useful. Ask us to delete it and we will.',
      },
      {
        h: 'Your rights',
        p:
          'Under the Norwegian Personal Data Act and the GDPR, you can ask to see what we have, correct it, delete it, or take it elsewhere. Write to hei@solrick.com — we’ll respond within a month. If you think we’re handling your data badly, you can complain to Datatilsynet at datatilsynet.no, the Norwegian data protection authority.',
      },
    ],
    updated: 'Last updated 15 May 2026',
    back: 'Back',
    meta: 'Wine import — Oslo, Norway',
  },
  no: {
    title: 'PERSONVERN',
    intro:
      'Vi er Solrick — en liten norsk vinimportør. Denne siden beskriver hva som skjer med informasjonen din når du bruker kontaktskjemaet vårt.',
    sections: [
      {
        h: 'Hva vi samler inn',
        p:
          'Bare det du skriver i skjemaet — navn, e-postadresse og melding. Ingenting annet. Vi bruker ikke analyseverktøy, vi setter ikke informasjonskapsler, vi deler ikke data med sosiale nettverk, og vi sporer deg ikke på tvers av nettet.',
      },
      {
        h: 'Hva vi gjør med det',
        p:
          'Vi leser meldingen din og svarer. Det er hele hensikten. Det rettslige grunnlaget er berettiget interesse — du tok kontakt, vi tar kontakt tilbake.',
      },
      {
        h: 'Hvor det ligger',
        p:
          'Meldingen din går gjennom Amazon Web Services — hostingleverandøren vår, som opptrer som vår databehandler — og havner i innboksen vår på hei@solrick.com. Vi lagrer ikke skjemainnsendinger i noen database; de ligger i e-posten vår, som annen e-post. AWS er sertifisert under EU–US Data Privacy Framework, som dekker den korte passasjen gjennom deres amerikanske infrastruktur.',
      },
      {
        h: 'Hvor lenge vi har det',
        p:
          'Så lenge e-posttråden er nyttig. Be oss om å slette den, og vi gjør det.',
      },
      {
        h: 'Rettighetene dine',
        p:
          'Etter personopplysningsloven og GDPR kan du be om å se hva vi har, rette det, slette det, eller flytte det. Skriv til hei@solrick.com — vi svarer innen en måned. Mener du vi behandler dataene dine dårlig, kan du klage til Datatilsynet på datatilsynet.no.',
      },
    ],
    updated: 'Sist oppdatert 15. mai 2026',
    back: 'Tilbake',
    meta: 'Vinimport — Oslo, Norge',
  },
} satisfies Record<Lang, {
  title: string;
  intro: string;
  sections: { h: string; p: string }[];
  updated: string;
  back: string;
  meta: string;
}>;

function readStoredLang(): Lang {
  try {
    const v = localStorage.getItem('solrick-lang');
    return v === 'no' ? 'no' : 'en';
  } catch {
    return 'en';
  }
}

export default function PrivacyPolicy() {
  const [lang, setLang] = useState<Lang>(readStoredLang);
  const t = STRINGS[lang];

  useEffect(() => {
    try { localStorage.setItem('solrick-lang', lang); } catch {}
    document.documentElement.lang = lang === 'no' ? 'nb' : 'en';
    document.title = lang === 'no' ? 'Solrick — Personvern' : 'Solrick — Privacy';
  }, [lang]);

  return (
    <main className="pp-stage">
      <header className="pp-top">
        <a className="pp-corner-mark" href="/" aria-label="Solrick">
          <img src="/logo-text-only.svg" alt="Solrick" />
        </a>
        <div className="pp-lang" role="group" aria-label="Language">
          <button
            type="button"
            className={'pp-lang-btn' + (lang === 'en' ? ' is-on' : '')}
            onClick={() => setLang('en')}
            aria-pressed={lang === 'en'}
          >
            EN
          </button>
          <span className="pp-lang-sep" aria-hidden>/</span>
          <button
            type="button"
            className={'pp-lang-btn' + (lang === 'no' ? ' is-on' : '')}
            onClick={() => setLang('no')}
            aria-pressed={lang === 'no'}
          >
            NO
          </button>
        </div>
      </header>

      <article className="pp-content">
        <h1 className="pp-headline">{t.title}</h1>
        <p className="pp-intro t-italic">{t.intro}</p>

        {t.sections.map(s => (
          <section className="pp-section" key={s.h}>
            <h2 className="pp-h2">{s.h}</h2>
            <p className="pp-p">{s.p}</p>
          </section>
        ))}

        <p className="pp-updated">{t.updated}</p>
      </article>

      <footer className="pp-bottom">
        <a className="pp-back" href="/">
          <span className="arrow" aria-hidden>←</span>
          <span>{t.back}</span>
        </a>
        <span className="pp-bottom-meta">{t.meta}</span>
      </footer>
    </main>
  );
}
